// src/components/common/Footer/Footer.tsx (Notion API対応版)
import React, { useState, useEffect, useRef } from 'react';
import { useProject } from '../../../hooks/useNotionProjects';
import './Footer.css';

interface FooterProps {
  currentPage?: 'home' | 'project' | 'profile';
  projectId?: string;
}

const Footer: React.FC<FooterProps> = ({ currentPage = 'home', projectId }) => {
  const [isTransitioning] = useState(false);
  const [isWelcomeExpanded, setIsWelcomeExpanded] = useState(false);
  const titleRef = useRef<HTMLSpanElement>(null);

  // プロジェクトデータを取得
  const { project, loading: projectLoading } = useProject(projectId || '');

  // デバッグ用
  useEffect(() => {
    if (currentPage === 'project') {
      console.log('🔍 Footer Debug:', {
        currentPage,
        projectId,
        projectLoading,
        project,
        projectTitle: project?.title,
        siteUrl: project?.url
      });
    }
  }, [currentPage, projectId, project, projectLoading]);

  // フッタータイトルのフォントサイズを動的調整
  useEffect(() => {
    const adjustTitleFontSize = () => {
      if (titleRef.current && currentPage === 'project') {
        const container = titleRef.current.parentElement;
        if (!container) return;

        const containerWidth = container.offsetWidth;

        // 初期フォントサイズ18pxから開始
        titleRef.current.style.fontSize = '18px';

        // テキストが溢れているかチェック
        const textWidth = titleRef.current.scrollWidth;

        if (textWidth > containerWidth) {
          // 溢れている場合のみフォントサイズを縮小
          const ratio = containerWidth / textWidth;
          const newFontSize = Math.max(12, 18 * ratio * 0.95); // 最小12px
          titleRef.current.style.fontSize = `${newFontSize}px`;
        }
      }
    };

    adjustTitleFontSize();
    window.addEventListener('resize', adjustTitleFontSize);
    return () => window.removeEventListener('resize', adjustTitleFontSize);
  }, [project?.title, currentPage]);
  
  const handleNavigation = (direction: 'prev' | 'next') => {
    if (isTransitioning) return;
    
    const btnId = direction === 'prev' ? 'carousel-prev' : 'carousel-next';
    const btn = document.getElementById(btnId);
    if (btn) {
      btn.click();
    }
  };

  // メール送信ハンドラー
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    const emailAddress = 'hello@osada.com';
    const subject = 'Portfolio Inquiry';
    const body = `Hi OSADA,

I visited your portfolio website and would like to get in touch regarding:

[ Please describe your inquiry here ]

Best regards,
[ Your name ]`;

    const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  // 外部サイトへのリンクハンドラー
  const handleSiteClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // website_url または app_store_url のいずれかを開く
    const urlToOpen = project?.url || project?.appStoreUrl;
    if (urlToOpen) {
      // 新しいタブで外部サイトを開く
      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    }
  };

  if (currentPage === 'project') {
    // プロジェクトタイトルの決定
    const displayTitle = projectLoading
      ? 'Loading...'
      : (project?.title || 'Project');

    // サイトURLまたはApp Store URLがあるかチェック
    const hasSiteUrl = (project?.url && project.url.trim() !== '') ||
                       (project?.appStoreUrl && project.appStoreUrl.trim() !== '');
    
    return (
      <footer className="footer">
        <div className="footer-left">
          <button
            className="project-nav-button"
            onClick={() => handleNavigation('prev')}
            disabled={isTransitioning || projectLoading}
          >
            <span className="nav-text">← PREVIOUS</span>
            <span className="nav-arrow-only">←</span>
          </button>
        </div>

        <div className="footer-center">
          {hasSiteUrl ? (
            // サイトURLがある場合：リンクとして表示
            <button
              onClick={handleSiteClick}
              className="project-title-footer-link"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                margin: 0
              }}
              title={`Visit ${displayTitle}`}
            >
              <span ref={titleRef} className="project-title-footer">
                {displayTitle} <span className="project-title-arrow">↗</span>
              </span>
            </button>
          ) : (
            // サイトURLがない場合：テキストのみ（矢印なし）
            <div className="project-title-footer-link">
              <span ref={titleRef} className="project-title-footer">
                {displayTitle}
              </span>
            </div>
          )}
        </div>

        <div className="footer-right">
          <button
            className="project-nav-button"
            onClick={() => handleNavigation('next')}
            disabled={isTransitioning || projectLoading}
          >
            <span className="nav-text">NEXT →</span>
            <span className="nav-arrow-only">→</span>
          </button>
        </div>
      </footer>
    );
  }
  
  // WELCOMEのタップハンドラー
  const handleWelcomeClick = () => {
    setIsWelcomeExpanded(!isWelcomeExpanded);
  };

  // ホームページとプロファイルページで同じフッターを表示
  return (
    <footer className="footer">
      <div className="footer-left">
        <div
          className={`nav-link hover-text-effect ${isWelcomeExpanded ? 'expanded' : ''}`}
          onClick={handleWelcomeClick}
          style={{ cursor: 'pointer' }}
        >
          <span className="hover-text-top">WELCOME!</span>
          <span className="hover-text-bottom">ENJOY☕️</span>
        </div>
      </div>

      <nav className="footer-right">
        <button
          className="nav-link contact-button hover-text-effect"
          onClick={handleContactClick}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            margin: 0
          }}
        >
          <span className="hover-text-top">SEND MESSAGE？</span>
          <span className="hover-text-bottom">SEND MESSAGE？</span>
        </button>
      </nav>
    </footer>
  );
};

export default Footer;